import React, { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { usePreviewState } from './usePreviewState';
import { useIsMimicking } from './useIsMimicking';
import { useLoadPreviewData } from './useLoadPreviewData';
import { useLinkInterception } from './useLinkInterception';
import { createPreviewApiClient } from './apiClient';
import { LoadingState } from './LoadingState';
import { ErrorState } from './ErrorState';
import { EmptyState } from './EmptyState';
import { PreviewContent } from './PreviewContent';
import { shouldRenderEmptyPreview } from './shouldRenderEmptyPreview';
import type { Page } from '../../schemas/validation';

export const PagePreview: React.FC = () => {
  const { siteId, pageId } = useParams<{ siteId: string; pageId: string }>();
  const [searchParams] = useSearchParams();
  const isMimicking = useIsMimicking();
  const state = usePreviewState();
  const isEmbedded = searchParams.get('embed') === 'true';

  const { loadPreviewData } = useLoadPreviewData({ siteId, pageId, state });

  useEffect(() => {
    if (!siteId) return;

    const authToken = localStorage.getItem('authToken');
    const user = localStorage.getItem('user');

    if (!authToken || !user) {
      console.warn('🔍 Preview Debug: No authentication found, redirecting to login');
      state.setError('Please log in to preview pages');
      return;
    }

    loadPreviewData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, pageId]);

  useEffect(() => {
    if (!siteId || !state.previewData) return;

    const loadSitePages = async () => {
      try {
        const apiClient = createPreviewApiClient();
        const response = await apiClient.get<Page[]>(`/sites/${siteId}/pages`);
        state.setSitePages(response.data);
      } catch (err) {
        console.warn('Failed to load site pages for navigation:', err);
      }
    };

    loadSitePages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, state.previewData]);

  useLinkInterception({
    isEmbedded,
    siteId,
    sitePages: state.sitePages,
  });

  if (state.isLoading) {
    return <LoadingState isMimicking={isMimicking} />;
  }

  if (state.error) {
    return <ErrorState error={state.error} isMimicking={isMimicking} onRetry={loadPreviewData} />;
  }

  if (!state.previewData || shouldRenderEmptyPreview(state.previewData)) {
    return <EmptyState previewData={state.previewData} isMimicking={isMimicking} />;
  }

  return (
    <PreviewContent
      previewData={state.previewData}
      isEmbedded={isEmbedded}
      isMimicking={isMimicking}
    />
  );
};

export default PagePreview;
